import {
  CreateUserResponse as CreateUserType,
  GoogleSignInServerAuthResponse as GoogleSignInServerAuthType,
  MicrosoftSignInResponse as MicrosoftSignInType,
} from '@otter-ai/recall-client';
import {
  AccountType,
  ConversationAuthorizationType,
  EmailDomainType,
  EventReturnTypes,
  identifyUser,
  OnboardSignupSuccessEvent,
  ReferralType,
} from '@otter-ai/telemetry';
import { useCallback } from 'react';
import { DAY_MS } from '#constants';
import { CheckEmailApiResponse } from '#mutation';
import {
  emailDomainLabelToDomainType,
  emailHostToProviderType,
  getFromLocalStorage,
  LOCAL_STORAGE_KEYS,
  removeFromLocalStorage,
} from '#utils';
import { useTrackEventWithGeoLocation } from './useTrackEventWithGeoLocation.ts';

type OtterLRUPublicSpeech = {
  ts?: number;
  otid?: string;
  speechType?: string;
  ConversationAuthorizationType?: ConversationAuthorizationType;
};

function getLRUPublicSpeechTrackingInfo() {
  const otterLRUPublicSpeech = getFromLocalStorage<OtterLRUPublicSpeech>(
    LOCAL_STORAGE_KEYS.OTTER_LRU_PUBLIC_SPEECH,
  );

  let referralType: ReferralType | undefined;
  let conversationID: string | undefined;
  let conversationAuthorizationType: ConversationAuthorizationType | undefined;

  if (
    otterLRUPublicSpeech?.otid &&
    otterLRUPublicSpeech.ts &&
    Date.now() - otterLRUPublicSpeech.ts < DAY_MS
  ) {
    referralType = 'conversation';
    conversationID = `${otterLRUPublicSpeech.speechType}: ${otterLRUPublicSpeech.otid}`;
    conversationAuthorizationType =
      otterLRUPublicSpeech.ConversationAuthorizationType;
  }

  removeFromLocalStorage(LOCAL_STORAGE_KEYS.OTTER_LRU_PUBLIC_SPEECH);

  return { referralType, conversationID, conversationAuthorizationType };
}

/** Retrieves event properties for referrals */
function getConversationReferralProperties(): Partial<
  EventReturnTypes['Onboard_SignupSuccess']['properties']
> {
  const { referralType, conversationID, conversationAuthorizationType } =
    getLRUPublicSpeechTrackingInfo();
  return {
    ...(referralType && {
      ReferralType: referralType,
    }),
    ...(conversationID && {
      ConversationID: conversationID,
    }),
    ...(conversationAuthorizationType && {
      ConversationAuthorizationType: conversationAuthorizationType,
    }),
  };
}

/** Retrieves event properties related to email */
function getEmailTrackingProperties(
  checkEmailResp: CheckEmailApiResponse,
): Partial<EventReturnTypes['Onboard_SignupSuccess']['properties']> {
  if (!checkEmailResp.data) return {};
  const { email_host, domain_label } = checkEmailResp.data;
  const emailProvider = emailHostToProviderType(email_host);
  const emailDomainType = emailDomainLabelToDomainType(domain_label);
  return {
    EmailProvider: emailProvider ?? undefined,
    EmailDomainType: emailDomainType ?? EmailDomainType.unknown,
  };
}

/**
 * Hook for tracking onboard signup events.
 */
export function useOnboardSignupAnalytics() {
  const { trackEventWithGeoIP } = useTrackEventWithGeoLocation();
  const logOnboardSignupSuccessEvent = useCallback(
    (
      data: {
        authResp:
          | GoogleSignInServerAuthType
          | MicrosoftSignInType
          | CreateUserType;
        checkEmailResp?: CheckEmailApiResponse | null;
        accountType: AccountType;
        SourceCodeID: string;
      },
      options?: { checkReferral?: boolean },
    ) => {
      const { authResp, checkEmailResp, accountType, SourceCodeID } = data;
      const { checkReferral = false } = options ?? {};

      identifyUser(authResp.user);
      trackEventWithGeoIP(
        OnboardSignupSuccessEvent({
          AccountType: accountType,
          SourceCodeID: SourceCodeID,
          UserID: authResp.user?.id,
          ...(checkReferral && getConversationReferralProperties()),
          ...(checkEmailResp && getEmailTrackingProperties(checkEmailResp)),
        }),
        { user: authResp.user },
      );
    },
    [trackEventWithGeoIP],
  );

  return { logOnboardSignupSuccessEvent };
}
